/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import * as yup from "yup";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import { useLazyGetByCpQuery } from "@api/postalCodes";
import { useLazyGetAddressTypeQuery, useGetBusinessActivitiesQuery } from "@api/catalogs";
import { useLazyGetAddressQuery, usePatchAddressMutation, usePostAddressMutation } from "@api/address";
import { Address, AddressData, PayloadAccountPatch, ColoniaStructure } from "@interfaces/index";
import { InactivityHandler } from "@components/InactivityHandler";
import { ADDRESS_CODES } from "@helpers/constantes";
import { useLazyGetLoanApplicationByIdQuery } from "@api/applications";
import { useLazyGetCuentasByIdQuery, usePatchCuentaMutation } from "@api/account";
import { UUID } from "crypto";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { ApiError } from "@interfaces/response";
import CustomRightImage from "@components/CustomRightImagev2";
import * as Sentry from "@sentry/react";
import useFormikErrors from "@hooks/useFormikErrors";
import useSolicitudCreditoForm from "@hooks/useSolicitudCreditoForm";
import CustomContryCode from "@components/CustomContryCode/CustomContryCode";
import { PHONE_NUMBER } from "@utils/expreciones";

const SolicitudCreditoPart1: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [getByCp] = useLazyGetByCpQuery();
  const [postAddress] = usePostAddressMutation();
  const [patchAddress] = usePatchAddressMutation();
  const businessActivities = useGetBusinessActivitiesQuery();

  const { isVisible, isRequired, requiredField } = useSolicitudCreditoForm("step_1");

  const application = useSelector((state: RootState) => state.register.application);
  const clientDetails = useSelector((state: RootState) => state.register.personal_data);

  const [addresType, setAddresType] = useState<Address | undefined>(undefined);
  const [colonias, setColonias] = useState<{ id: number; name: string }[]>([]);
  const [address, setAddress] = useState<AddressData | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [getAddress] = useLazyGetAddressQuery();
  const [patchAccount] = usePatchCuentaMutation();
  const [getAddresType] = useLazyGetAddressTypeQuery();
  const [getApplicationById] = useLazyGetLoanApplicationByIdQuery();
  const [getCuentaById] = useLazyGetCuentasByIdQuery();

  const handleOnClickRestart = () => {
    navigate("/producto/creditosimple/avance_expediente_digital");
  };

  const formik = useFormik({
    initialValues: {
      country_code: "",
      cell_phone_number: "",
      giro: "",
      descripcion: "",
      codigoPostal: "",
      calle: "",
      noInterior: "",
      noExterior: "",
      estadoId: "",
      ciudadId: "",
      localidadId: "",
      coloniaId: "",
      estado: "",
      ciudad: "",
      localidad: "",
      otro: "",
    },
    validationSchema: yup.object({
      country_code: requiredField(yup.string(), "cell_phone_number", true),
      cell_phone_number: requiredField(
        yup.string().matches(PHONE_NUMBER, "Número de teléfono inválido"),
        "cell_phone_number",
        true,
      ),
      giro: requiredField(yup.string(), "giro", true, "Requerido"),
      descripcion: requiredField(yup.string(), "descripcion", true, "Requerido"),
      codigoPostal: requiredField(
        yup.string().matches(/^[0-9]{5}$/, "Debe ser un código postal válido"),
        "domicilio_operativo",
        true,
        "Requerido",
      ),
      calle: requiredField(yup.string(), "domicilio_operativo", true, "Requerido"),
      noInterior: requiredField(yup.string(), "domicilio_operativo", false),
      noExterior: requiredField(yup.string(), "domicilio_operativo", true, "Requerido"),
      // estadoId/localidadId no tienen input propio: dependen de que el bloque de domicilio esté activo.
      estadoId: requiredField(yup.string(), "domicilio_operativo", true, "Requerido"),
      ciudadId: yup.string(),
      coloniaId: requiredField(yup.string(), "domicilio_operativo", true, "Requerido"),
      localidadId: requiredField(yup.string(), "domicilio_operativo", true, "Requerido"),
      otro: yup.string().when("coloniaId", {
        is: "-1",
        then: (schema) => requiredField(schema, "domicilio_operativo", true),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const payload_account: PayloadAccountPatch = {};

        if (isVisible("cell_phone_number")) {
          payload_account.country_code = values.country_code;
          payload_account.cell_phone_number = values.cell_phone_number;
        }
        if (isVisible("giro")) {
          payload_account.business_activity_id = values.giro;
        }
        if (isVisible("descripcion")) {
          payload_account.business_description = values.descripcion;
        }

        await patchAccount({
          accountId: application.client_details_id,
          body: payload_account,
        }).unwrap();

        if (isVisible("domicilio_operativo")) {
          const payload_address = {
            client_details_id: clientDetails.id,
            address_type_id: addresType?.id,
            cp: values.codigoPostal,
            street: values.calle,
            no_ext: values.noExterior,
            no_int: values.noInterior,
            country_id: 1,
            state_id: values.estadoId ? +values.estadoId : null,
            city_id: values.ciudadId ? +values.ciudadId : null,
            locality_id: values.localidadId ? +values.localidadId : null,
            colonia_id: Number(values.coloniaId) === -1 ? null : Number(values.coloniaId),
            other_neighborhood: values.otro,
          };

          if (address?.id) {
            await patchAddress({
              addressId: address.id as UUID,
              data: payload_address,
            }).unwrap();
          } else {
            await postAddress(payload_address).unwrap();
          }
        }

        navigate("/producto/creditosimple/solicitud_credito_2");
      } catch (error) {
        const api_error = error as ApiError;

        Sentry.captureException(error, {
          extra: {
            component: "SolicitudCreditoPart1-submit",
          },
        });

        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const { validateMultipleFields } = useFormikErrors({ formik });

  const saveCompanyData = async (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    try {
      formik.handleBlur(e);

      if (!application.client_details_id) {
        return;
      }

      const isValid = validateMultipleFields(["country_code", "cell_phone_number", "giro", "descripcion"]);
      if (!isValid) {
        return;
      }

      await patchAccount({
        accountId: application.client_details_id,
        body: {
          country_code: formik.values.country_code,
          cell_phone_number: formik.values.cell_phone_number,
          business_activity_id: formik.values.giro || null,
          business_description: formik.values.descripcion,
        },
      }).unwrap();
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          component: "SolicitudCreditoPart1-saveCompanyData",
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    }
  };

  const saveAddressDataOperative = async (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    try {
      formik.handleBlur(e);

      const isValid = validateMultipleFields([
        "codigoPostal",
        "calle",
        "noExterior",
        "noInterior",
        "coloniaId",
        "otro",
      ]);
      if (!isValid) {
        return;
      }

      const payload_address = {
        client_details_id: clientDetails.id,
        address_type_id: addresType?.id,
        cp: formik.values.codigoPostal,
        street: formik.values.calle,
        no_ext: formik.values.noExterior,
        no_int: formik.values.noInterior,
        country_id: 1,
        state_id: formik.values.estadoId ? +formik.values.estadoId : null,
        city_id: formik.values.ciudadId ? +formik.values.ciudadId : null,
        locality_id: formik.values.localidadId ? +formik.values.localidadId : null,
        colonia_id: Number(formik.values.coloniaId) === -1 ? null : Number(formik.values.coloniaId),
        other_neighborhood: formik.values.otro,
      };

      if (address?.id) {
        await patchAddress({
          addressId: address.id as UUID,
          data: payload_address,
        }).unwrap();
      } else {
        const newAddress = await postAddress(payload_address).unwrap();
        setAddress(newAddress.data);
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          component: "SolicitudCreditoPart1-saveAddressData",
        },
      });
    }
  };

  const fetchLocationData = async (codigoPostal: string, coloniaId?: number) => {
    try {
      if (codigoPostal.length === 5) {
        const value = await getByCp(codigoPostal).unwrap();
        const { colonias, cities, states, localities } = value;
        setColonias(colonias);

        formik.setFieldValue("estadoId", states[0]?.id || "");
        formik.setFieldValue("estado", states[0]?.name || "");
        formik.setFieldValue("ciudadId", cities[0]?.id || "");
        formik.setFieldValue("ciudad", cities[0]?.name || "");
        formik.setFieldValue("localidadId", localities[0]?.id || "");
        formik.setFieldValue("localidad", localities[0]?.name || "");

        if (coloniaId) {
          const coloniaSelected = colonias.find((item: ColoniaStructure) => item.id == coloniaId);
          formik.setFieldValue("coloniaId", coloniaSelected?.id || "");
        } else {
          formik.setFieldValue("coloniaId", colonias[0]?.id || "");
        }

        setTimeout(() => {
          formik.validateForm();
        }, 200);
      } else {
        formik.setFieldValue("estadoId", "");
        formik.setFieldValue("estado", "");
        formik.setFieldValue("ciudadId", "");
        formik.setFieldValue("ciudad", "");
        formik.setFieldValue("localidadId", "");
        formik.setFieldValue("localidad", "");
        formik.setFieldValue("coloniaId", "");
        formik.setFieldValue("colonia", "");
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          component: "SolicitudCreditoPart1-fetchLocationData",
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    }
  };

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        if (!application) {
          throw new Error("Solicitud no encontrada");
        }

        const [loan_application, res_address_type, res_account, res_address] = await Promise.all([
          getApplicationById(application.id).unwrap(),
          getAddresType().unwrap(),
          getCuentaById({ id: application.client_details_id }).unwrap(),
          getAddress({
            accountId: application.client_details_id,
          }).unwrap(),
        ]);

        const address_type = res_address_type.find((item: Address) => item.code === ADDRESS_CODES.operativo.code);
        const address = res_address.find((item: AddressData) => item.address_type_id === address_type?.id);
        const account = res_account.data;

        if (!loan_application) {
          throw new Error("No se encontró la solicitud de crédito");
        }

        setAddresType(address_type);
        setAddress(address);
        formik.setFieldValue("country_code", account.country_code || "");
        formik.setFieldValue("cell_phone_number", account.cell_phone_number || "");
        formik.setFieldValue("giro", account.business_activity_id || "");
        formik.setFieldValue("descripcion", account.business_description || "");

        if (address) {
          formik.setFieldValue("codigoPostal", address.cp || "");
          formik.setFieldValue("calle", address.street || "");
          formik.setFieldValue("noInterior", address.no_int || "");
          formik.setFieldValue("noExterior", address.no_ext || "");
          formik.setFieldValue("otro", address.other_neighborhood || "");
          fetchLocationData(address.cp, address.colonia_id || -1);
        }

        setTimeout(() => {
          formik.validateForm();
        }, 200);
      } catch (error) {
        const api_error = error as ApiError;
        Sentry.captureException(error, {
          extra: {
            component: "SolicitudCreditoPart1-initial_fetch",
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (String(formik.values.coloniaId) !== "-1") {
      formik.setFieldValue("otro", "");
    }
  }, [formik.values.coloniaId]);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8} mt={4}>
        <InactivityHandler />
        <Box maxWidth="sm" mx="auto" mt={4} px={2}>
          <Typography
            textAlign={"center"}
            variant={isMobileOrTablet ? "body2" : "body1"}
            fontWeight={700}
            color={theme?.palette?.primary?.dark}
          >
            Solicitud de crédito
          </Typography>
          <Typography
            textAlign={"center"}
            variant={isMobileOrTablet ? "body2" : "body1"}
            color={theme?.palette?.primary?.dark}
            mt={2}
          >
            <strong>1</strong> de 3
          </Typography>

          <form onSubmit={formik.handleSubmit} noValidate>
            <Typography
              textAlign={"center"}
              variant={isMobileOrTablet ? "body2" : "body1"}
              color={theme?.palette?.primary?.dark}
              fontWeight={600}
              my={4}
            >
              Datos de la empresa:
            </Typography>

            <Grid container spacing={1} mt={2}>
              {isVisible("cell_phone_number") && (
                <Grid item xs={5} sm={5} md={5}>
                  <CustomContryCode
                    label="Código"
                    name="country_code"
                    value={formik.values.country_code}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.country_code && Boolean(formik.errors.country_code)}
                    disabled={formik.isSubmitting}
                    required={isRequired("cell_phone_number", true)}
                  />
                </Grid>
              )}

              {isVisible("cell_phone_number") && (
                <Grid item xs={7} sm={7} md={7}>
                  <TextField
                    type="text"
                    name="cell_phone_number"
                    variant="outlined"
                    label="Teléfono"
                    fullWidth
                    value={formik.values.cell_phone_number}
                    required={isRequired("cell_phone_number", true)}
                    onChange={(event) => {
                      formik.setFieldValue("cell_phone_number", event.target.value.replace(/[^0-9]/g, ""));
                    }}
                    onBlur={formik.handleBlur}
                    error={formik.touched.cell_phone_number && Boolean(formik.errors.cell_phone_number)}
                    helperText={formik.touched.cell_phone_number && formik.errors.cell_phone_number}
                    inputProps={{
                      maxLength: 10,
                      inputMode: "numeric",
                    }}
                    disabled={formik.isSubmitting}
                  />
                </Grid>
              )}
            </Grid>

            {isVisible("giro") && (
              <FormControl fullWidth required={isRequired("giro", true)} sx={{ mt: 2 }}>
                <InputLabel id={"giro"}>Giro</InputLabel>
                <Select
                  labelId="giro"
                  name="giro"
                  id="giro"
                  fullWidth
                  required={isRequired("giro", true)}
                  label="Giro"
                  value={formik.values.giro}
                  onBlur={saveCompanyData}
                  onChange={formik.handleChange}
                  error={formik.touched.giro && Boolean(formik.errors.giro)}
                  sx={{ textAlign: "left" }}
                  disabled={businessActivities.isLoading || isLoading || formik.isSubmitting}
                >
                  <MenuItem value="">Selecciona</MenuItem>
                  {businessActivities.isLoading ? (
                    <MenuItem value="">Cargando...</MenuItem>
                  ) : (
                    businessActivities.currentData
                      ?.slice()
                      .sort((a, b) => a.description.localeCompare(b.description))
                      .map((activity) => (
                        <MenuItem key={activity.id} value={activity.id}>
                          {activity.description}
                        </MenuItem>
                      ))
                  )}
                </Select>
                {formik.touched.giro && formik.errors.giro && <FormHelperText>{formik.errors.giro}</FormHelperText>}
              </FormControl>
            )}

            {isVisible("descripcion") && (
              <FormControl fullWidth>
                <TextField
                  fullWidth
                  id="descripcion"
                  name="descripcion"
                  label="Describe brevemente a qué se dedica tu empresa"
                  required={isRequired("descripcion", true)}
                  value={formik.values.descripcion}
                  onChange={formik.handleChange}
                  onBlur={saveCompanyData}
                  error={formik.touched.descripcion && Boolean(formik.errors.descripcion)}
                  helperText={formik.touched.descripcion ? formik.errors.descripcion : ""}
                  margin="normal"
                  multiline
                  rows={3}
                  disabled={isLoading || formik.isSubmitting}
                  inputProps={{ maxLength: 500 }}
                />
              </FormControl>
            )}

            {isVisible("domicilio_operativo") && (
              <>
                <Typography
                  textAlign={"center"}
                  variant={isMobileOrTablet ? "body2" : "body1"}
                  color={theme?.palette?.primary?.dark}
                  fontWeight={600}
                  my={4}
                >
                  Domicilio operativo:
                </Typography>

                <FormControl fullWidth>
                  <TextField
                    fullWidth
                    id="codigoPostal"
                    name="codigoPostal"
                    label="Código Postal"
                    required={isRequired("domicilio_operativo", true)}
                    value={formik.values.codigoPostal}
                    onChange={(e) => {
                      const cp = e.target.value.replace(/[^0-9]/g, "");
                      formik.setFieldValue("codigoPostal", cp);
                      if (cp.length === 5) {
                        fetchLocationData(cp);
                      }
                    }}
                    onBlur={saveAddressDataOperative}
                    error={formik.touched.codigoPostal && Boolean(formik.errors.codigoPostal)}
                    helperText={formik.touched.codigoPostal ? formik.errors.codigoPostal : ""}
                    margin="normal"
                    disabled={isLoading || formik.isSubmitting}
                    inputProps={{ maxLength: 5 }}
                  />
                </FormControl>

                <FormControl fullWidth>
                  <TextField
                    fullWidth
                    id="calle"
                    name="calle"
                    label="Calle o avenida"
                    required={isRequired("domicilio_operativo", true)}
                    value={formik.values.calle}
                    onChange={formik.handleChange}
                    onBlur={saveAddressDataOperative}
                    error={formik.touched.calle && Boolean(formik.errors.calle)}
                    helperText={formik.touched.calle ? formik.errors.calle : ""}
                    margin="normal"
                    disabled={isLoading || formik.isSubmitting}
                    inputProps={{ maxLength: 100 }}
                  />
                </FormControl>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <FormControl fullWidth>
                      <TextField
                        fullWidth
                        id="noExterior"
                        name="noExterior"
                        label="No. Exterior"
                        required={isRequired("domicilio_operativo", true)}
                        value={formik.values.noExterior}
                        onChange={formik.handleChange}
                        onBlur={saveAddressDataOperative}
                        error={formik.touched.noExterior && Boolean(formik.errors.noExterior)}
                        helperText={formik.touched.noExterior ? formik.errors.noExterior : ""}
                        margin="normal"
                        disabled={isLoading || formik.isSubmitting}
                        inputProps={{ maxLength: 50 }}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth>
                      <TextField
                        fullWidth
                        id="noInterior"
                        name="noInterior"
                        label="No. Interior"
                        required={isRequired("domicilio_operativo", false)}
                        value={formik.values.noInterior}
                        onChange={formik.handleChange}
                        onBlur={saveAddressDataOperative}
                        error={formik.touched.noInterior && Boolean(formik.errors.noInterior)}
                        helperText={formik.touched.noInterior ? formik.errors.noInterior : ""}
                        margin="normal"
                        disabled={isLoading || formik.isSubmitting}
                        inputProps={{ maxLength: 50 }}
                      />
                    </FormControl>
                  </Grid>
                </Grid>

                <FormControl fullWidth required={isRequired("domicilio_operativo", true)} sx={{ mt: 1 }}>
                  <InputLabel id={"coloniaId"}>Colonia</InputLabel>
                  <Select
                    labelId="coloniaId"
                    name="coloniaId"
                    id="coloniaId"
                    fullWidth
                    required={isRequired("domicilio_operativo", true)}
                    label="Colonia"
                    value={formik.values.coloniaId}
                    onBlur={saveAddressDataOperative}
                    onChange={formik.handleChange}
                    error={formik.touched.coloniaId && Boolean(formik.errors.coloniaId)}
                    sx={{ textAlign: "left" }}
                    disabled={isLoading || formik.isSubmitting}
                  >
                    <MenuItem value="">Selecciona</MenuItem>
                    {colonias.map((colonia) => (
                      <MenuItem key={colonia.id} value={colonia.id}>
                        {colonia.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.coloniaId && formik.errors.coloniaId && (
                    <FormHelperText>{formik.errors.coloniaId}</FormHelperText>
                  )}
                </FormControl>

                {String(formik.values.coloniaId) === "-1" && (
                  <FormControl fullWidth required={isRequired("domicilio_operativo", true)}>
                    <TextField
                      id="otro"
                      name="otro"
                      label="Otra Colonia"
                      margin="normal"
                      required={isRequired("domicilio_operativo", true)}
                      fullWidth
                      autoComplete="off"
                      onBlur={saveAddressDataOperative}
                      onChange={formik.handleChange}
                      error={formik.touched.otro && Boolean(formik.errors.otro)}
                      helperText={formik.touched.otro && formik.errors.otro}
                      value={formik.values.otro}
                      disabled={isLoading || formik.isSubmitting}
                    />
                  </FormControl>
                )}

                <FormControl fullWidth>
                  <TextField
                    fullWidth
                    id="ciudad"
                    name="ciudad"
                    label="Ciudad"
                    value={formik.values.ciudad}
                    error={formik.touched.ciudadId && Boolean(formik.errors.ciudadId)}
                    helperText={formik.touched.ciudadId ? formik.errors.ciudadId : ""}
                    margin="normal"
                    disabled={true}
                  />
                </FormControl>

                <FormControl fullWidth>
                  <TextField
                    fullWidth
                    id="localidadId"
                    name="localidadId"
                    label="Municipio"
                    value={formik.values.localidad}
                    error={formik.touched.localidadId && Boolean(formik.errors.localidadId)}
                    helperText={formik.touched.localidadId ? formik.errors.localidadId : ""}
                    margin="normal"
                    disabled={true}
                  />
                </FormControl>

                <FormControl fullWidth>
                  <TextField
                    fullWidth
                    id="estado"
                    name="estado"
                    label="Entidad federativa"
                    value={formik.values.estado}
                    error={formik.touched.estadoId && Boolean(formik.errors.estadoId)}
                    helperText={formik.touched.estadoId ? formik.errors.estadoId : ""}
                    margin="normal"
                    disabled={true}
                  />
                </FormControl>
              </>
            )}

            <Grid container columnSpacing={1} my={4}>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="blue_outlined"
                  sx={{ padding: "0", height: "100%" }}
                  onClick={handleOnClickRestart}
                  disabled={formik.isSubmitting}
                >
                  Regresar al inicio
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  type="submit"
                  variant="default"
                  fullWidth
                  color="primary"
                  disabled={!formik.isValid || formik.isSubmitting}
                >
                  Continuar
                </Button>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Grid>
      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default SolicitudCreditoPart1;
